import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  ArrowLeft, User, Phone, Mail, MapPin, Package, Calendar,
  Wifi, WifiOff, Activity, RefreshCw, Power, Settings,
  TrendingUp, TrendingDown, Clock, Signal, Router,
  Link as LinkIcon, Unlink, AlertCircle, CheckCircle, Ticket, Plus, X,
  Eye, EyeOff, Save, Users, Smartphone, Monitor, Laptop, Lock, Copy, Send, MessageCircle
} from 'lucide-react';
import { toast } from 'sonner';
import CustomerLocationCard from '../components/CustomerLocationCard';
import CreateInvoiceModal from '../components/CreateInvoiceModal';
import { Receipt } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Hide scrollbar styles
const hideScrollbarStyle = `
  .scrollbar-hide::-webkit-scrollbar {
    display: none;
  }
`;

// Custom Confirmation Modal Component
const ConfirmModal = ({ isOpen, onClose, onConfirm, title, message }) => {
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/50" onClick={onClose}></div>
      <div className="relative bg-white rounded-xl shadow-2xl max-w-md w-full mx-4 p-6 dark:bg-slate-800">
        <button 
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
        >
          <X size={20} />
        </button>
        
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-orange-100 rounded-full dark:bg-orange-900/40">
            <AlertCircle className="text-orange-600 dark:text-orange-400" size={24} />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-slate-100">{title}</h3>
        </div>
        
        <p className="text-gray-600 mb-6 whitespace-pre-line dark:text-slate-300">{message}</p>
        
        <div className="flex gap-3 justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition dark:text-slate-200 dark:bg-slate-800"
          >
            Batal
          </button>
          <button
            onClick={onConfirm}
            className="px-4 py-2 text-white bg-red-600 rounded-lg hover:bg-red-700 transition"
          >
            Ya, Reboot
          </button>
        </div>
      </div>
    </div>
  );
};

// WiFi Settings Modal Component
const WiFiSettingsModal = ({ isOpen, onClose, deviceId }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [wifiSettings, setWifiSettings] = useState(null);
  const [connectedDevices, setConnectedDevices] = useState([]);
  const [showPassword, setShowPassword] = useState({});
  const [editMode, setEditMode] = useState({});
  const [formData, setFormData] = useState({});

  useEffect(() => {
    if (isOpen && deviceId) {
      fetchWifiSettings();
      fetchConnectedDevices();
    }
  }, [isOpen, deviceId]);

  const getToken = () => {
    return localStorage.getItem('token');
  };

  const fetchWifiSettings = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${BACKEND_URL}/api/customer-wifi/${encodeURIComponent(deviceId)}/settings`,
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );

      if (response.data.success) {
        setWifiSettings(response.data.data);
        const initialForm = {};
        response.data.data.wifi_networks?.forEach((wifi, idx) => {
          initialForm[wifi.index] = {
            ssid: wifi.ssid,
            password: ''
          };
        });
        setFormData(initialForm);
      }
    } catch (error) {
      console.error('Error fetching WiFi settings:', error);
      toast.error('Gagal memuat pengaturan WiFi');
    } finally {
      setLoading(false);
    }
  };

  const fetchConnectedDevices = async () => {
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/customer-wifi/${encodeURIComponent(deviceId)}/connected`,
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );

      if (response.data.success) {
        setConnectedDevices(response.data.data.devices || []);
      }
    } catch (error) {
      console.error('Error fetching connected devices:', error);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    const toastId = toast.loading("Menghubungi ONT...");
    
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/customer-wifi/${encodeURIComponent(deviceId)}/refresh`,
        {},
        { headers: { Authorization: `Bearer ${getToken()}` }, timeout: 35000 }
      );
      
      toast.dismiss(toastId);
      
      if (response.data.success) {
        toast.success(response.data.message || "Refresh selesai!");
        fetchWifiSettings();
        fetchConnectedDevices();
      } else {
        toast.error(response.data.message || "Gagal refresh");
      }
    } catch (error) {
      toast.dismiss(toastId);
      console.error('Refresh error:', error);
      toast.error("Timeout atau gagal koneksi. Coba lagi.");
    }
    setRefreshing(false);
  };

  const handleSaveWifi = async (wifiIndex) => {
    const data = formData[wifiIndex];
    if (!data?.ssid && !data?.password) {
      toast.error('Masukkan nama WiFi atau password baru');
      return;
    }

    if (data.password && data.password.length < 8) {
      toast.error('Password minimal 8 karakter');
      return;
    }

    setSaving(true);
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/customer-wifi/${encodeURIComponent(deviceId)}/settings`,
        {
          wifi_index: wifiIndex,
          ssid: data.ssid || undefined,
          password: data.password || undefined
        },
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );

      if (response.data.success) {
        toast.success('Pengaturan WiFi berhasil diupdate! Perubahan akan diterapkan dalam 1-5 menit.');
        setEditMode({ ...editMode, [wifiIndex]: false });
        setFormData({
          ...formData,
          [wifiIndex]: { ...formData[wifiIndex], password: '' }
        });
        setTimeout(() => fetchWifiSettings(), 2000);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal menyimpan pengaturan');
    } finally {
      setSaving(false);
    }
  };

  const getDeviceIcon = (device) => {
    const hostname = device.hostname?.toLowerCase() || '';
    if (hostname.includes('iphone') || hostname.includes('android') || hostname.includes('realme') || hostname.includes('samsung') || hostname.includes('xiaomi') || hostname.includes('oppo') || hostname.includes('vivo')) {
      return <Smartphone className="w-5 h-5 text-blue-500" />;
    } else if (hostname.includes('laptop') || hostname.includes('macbook') || hostname.includes('notebook')) {
      return <Laptop className="w-5 h-5 text-gray-600 dark:text-slate-300" />;
    }
    return <Monitor className="w-5 h-5 text-gray-500 dark:text-slate-400" />;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4">
      <style>{hideScrollbarStyle}</style>
      <div className="fixed inset-0 bg-black/50" onClick={onClose}></div>
      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[95vh] overflow-hidden dark:bg-slate-800">
        {/* Header */}
        <div className="p-3 sm:p-4 border-b border-gray-200 flex items-center justify-between bg-gradient-to-r from-blue-50 to-indigo-50 dark:border-slate-700">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            <div className="p-1.5 sm:p-2 bg-blue-100 rounded-full shrink-0 dark:bg-blue-900/40">
              <Wifi className="text-blue-600 dark:text-blue-400" size={18} />
            </div>
            <div className="min-w-0">
              <h2 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-slate-100">Pengaturan WiFi</h2>
              <p className="text-xs sm:text-sm text-gray-500 truncate dark:text-slate-400">{deviceId}</p>
            </div>
          </div>
          <div className="flex items-center gap-1 sm:gap-2 shrink-0">
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className="flex items-center gap-1 px-2 py-1.5 text-xs sm:text-sm border border-gray-300 rounded-lg hover:bg-white transition-colors disabled:opacity-50 dark:border-slate-600"
            >
              <RefreshCw size={12} className={refreshing ? 'animate-spin' : ''} />
              <span className="hidden sm:inline">Refresh</span>
            </button>
            <button 
              onClick={onClose}
              className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg dark:hover:bg-slate-700"
            >
              <X size={18} />
            </button>
          </div>
        </div>
        
        {/* Content */}
        <div className="p-3 sm:p-4 overflow-y-auto max-h-[calc(95vh-70px)] scrollbar-hide" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-8 h-8 animate-spin text-blue-600 dark:text-blue-400" />
              <span className="ml-3 text-slate-600 dark:text-slate-300">Memuat pengaturan WiFi...</span>
            </div>
          ) : !wifiSettings || !wifiSettings.wifi_networks?.length ? (
            <div className="text-center py-12">
              <Wifi className="w-16 h-16 mx-auto text-slate-300 mb-4" />
              <h3 className="text-lg font-medium text-slate-700 dark:text-slate-200">Tidak ada data WiFi</h3>
              <p className="text-slate-500 mt-2 dark:text-slate-400">Device ID: {deviceId}</p>
              <button onClick={handleRefresh} className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700" disabled={refreshing}>
                <RefreshCw className={`w-4 h-4 mr-2 inline ${refreshing ? 'animate-spin' : ''}`} />
                Refresh Data
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {/* WiFi Networks */}
              <div className="grid gap-3">
                {wifiSettings.wifi_networks.map((wifi) => (
                  <div key={wifi.index} className="bg-gradient-to-br from-white to-blue-50 border border-blue-100 rounded-xl p-3 sm:p-4 shadow-sm">
                    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-2 mb-3">
                      <div>
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span className={`px-1.5 py-0.5 rounded text-[10px] sm:text-xs font-medium ${
                            wifi.band === '5GHz' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'
                          }`}>
                            {wifi.band}
                          </span>
                          <span className={`px-1.5 py-0.5 rounded text-[10px] sm:text-xs ${
                            wifi.enabled ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                          }`}>
                            {wifi.enabled ? 'Aktif' : 'Nonaktif'}
                          </span>
                          <span className="flex items-center gap-1 text-[10px] sm:text-xs text-slate-500 dark:text-slate-400">
                            <Users className="w-3 h-3" />
                            {wifi.connected_count}
                          </span>
                        </div>
                        <h3 className="text-sm sm:text-base font-semibold mt-1">{wifi.ssid}</h3>
                      </div>
                    </div>

                    {/* Current Settings */}
                    <div className="grid grid-cols-1 gap-1 mb-3 text-xs sm:text-sm">
                      <div className="flex justify-between">
                        <span className="text-slate-500 dark:text-slate-400">Channel:</span>
                        <span className="font-medium">{wifi.channel || 'Auto'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-500 dark:text-slate-400">Password:</span>
                        <span className="font-medium font-mono text-[10px] sm:text-xs">{wifi.password || "(tidak tersedia)"}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-500 dark:text-slate-400">Security:</span>
                        <span className="font-medium">{wifi.security}</span>
                      </div>
                    </div>

                    {/* Edit Form */}
                    {editMode[wifi.index] ? (
                      <div className="space-y-2 pt-3 border-t">
                        <div>
                          <label className="block text-xs font-medium text-slate-700 mb-1 dark:text-slate-200">
                            Nama WiFi (SSID)
                          </label>
                          <input
                            type="text"
                            value={formData[wifi.index]?.ssid || ''}
                            onChange={(e) => setFormData({
                              ...formData,
                              [wifi.index]: { ...formData[wifi.index], ssid: e.target.value }
                            })}
                            placeholder="Masukkan nama WiFi baru"
                            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-slate-600"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-slate-700 mb-1 dark:text-slate-200">
                            Password Baru
                          </label>
                          <div className="relative">
                            <input
                              type={showPassword[wifi.index] ? 'text' : 'password'}
                              value={formData[wifi.index]?.password || ''}
                              onChange={(e) => setFormData({
                                ...formData,
                                [wifi.index]: { ...formData[wifi.index], password: e.target.value }
                              })}
                              placeholder="Min 8 karakter"
                              className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 pr-8 dark:border-slate-600"
                            />
                            <button
                              type="button"
                              className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:text-slate-500"
                              onClick={() => setShowPassword({ ...showPassword, [wifi.index]: !showPassword[wifi.index] })}
                            >
                              {showPassword[wifi.index] ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                            </button>
                          </div>
                        </div>
                        <div className="flex gap-2 pt-1">
                          <button 
                            onClick={() => handleSaveWifi(wifi.index)}
                            disabled={saving}
                            className="flex items-center gap-1 px-3 py-1.5 text-xs bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                          >
                            <Save className="w-3.5 h-3.5" />
                            {saving ? 'Simpan...' : 'Simpan'}
                          </button>
                          <button 
                            onClick={() => setEditMode({ ...editMode, [wifi.index]: false })}
                            className="px-3 py-1.5 text-xs border border-gray-300 rounded-lg hover:bg-gray-50 dark:border-slate-600 dark:hover:bg-slate-700/50"
                          >
                            Batal
                          </button>
                        </div>
                      </div>
                    ) : (
                      <button 
                        onClick={() => setEditMode({ ...editMode, [wifi.index]: true })}
                        className="flex items-center justify-center gap-1 w-full px-3 py-2 text-xs sm:text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                      >
                        <Settings className="w-3.5 h-3.5" />
                        Ubah SSID / Password
                      </button>
                    )}
                  </div>
                ))}
              </div>

              {/* Connected Devices */}
              <div className="bg-gradient-to-br from-white to-green-50 border border-green-100 rounded-xl p-3 sm:p-4 shadow-sm">
                <h3 className="text-sm sm:text-base font-semibold mb-3 flex items-center gap-2">
                  <Users className="w-4 h-4 text-green-600 dark:text-green-400" />
                  Perangkat Terkoneksi ({connectedDevices.length})
                </h3>
                
                {connectedDevices.length === 0 ? (
                  <p className="text-slate-500 text-center py-3 text-xs sm:text-sm dark:text-slate-400">Tidak ada perangkat yang terkoneksi</p>
                ) : (
                  <div className="space-y-2">
                    {connectedDevices.map((device, idx) => (
                      <div key={idx} className="flex items-center justify-between py-2 border-b last:border-0">
                        <div className="flex items-center gap-2 min-w-0">
                          {getDeviceIcon(device)}
                          <div className="min-w-0">
                            <p className="font-medium text-slate-800 text-xs sm:text-sm truncate dark:text-slate-200">{device.hostname}</p>
                            <p className="text-[10px] sm:text-xs text-slate-500 truncate dark:text-slate-400">{device.mac}</p>
                          </div>
                        </div>
                        <div className="text-right shrink-0 ml-2">
                          <p className="text-xs font-medium text-slate-700 dark:text-slate-200">{device.ip}</p>
                          <p className={`text-[10px] ${device.connection_type === 'WiFi' ? 'text-blue-600' : 'text-green-600'}`}>
                            {device.connection_type}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Info Box */}
              <div className="bg-gradient-to-r from-blue-50 to-cyan-50 border border-blue-200 rounded-lg p-3">
                <p className="text-[10px] sm:text-xs text-blue-800">
                  <strong>Catatan:</strong> Perubahan akan diterapkan dalam 1-5 menit. Perangkat perlu dihubungkan ulang dengan password baru.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const CustomerDetail = () => {
  const [showCreateInvoice, setShowCreateInvoice] = useState(false);
  const [showPasswordCustomer, setShowPasswordCustomer] = useState(false);
  const { customerId } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [customer, setCustomer] = useState(null);
  const [monitoringData, setMonitoringData] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [refreshing, setRefreshing] = useState(false);
  const [availableDevices, setAvailableDevices] = useState([]);
  const [showLinkDialog, setShowLinkDialog] = useState(false);
  const [tickets, setTickets] = useState([]);
  const [ticketsLoading, setTicketsLoading] = useState(false);
  const [acsDeviceStatuses, setAcsDeviceStatuses] = useState({}); // Track ACS status per device
  const [rebootingDevices, setRebootingDevices] = useState({}); // Track rebooting per device
  const [showRebootConfirm, setShowRebootConfirm] = useState(false);
  const [pendingRebootDeviceId, setPendingRebootDeviceId] = useState(null);
  const [showWifiSettings, setShowWifiSettings] = useState(false);
  const [selectedDeviceForWifi, setSelectedDeviceForWifi] = useState(null);
  const [technicians, setTechnicians] = useState([]);
  const [techLoading, setTechLoading] = useState(false);
  const [showTechDropdown, setShowTechDropdown] = useState(false);
  const [techSearch, setTechSearch] = useState("");
  const [showCustomerMsgDropdown, setShowCustomerMsgDropdown] = useState(false);
  const [showManualForm, setShowManualForm] = useState(false);
  const [manualSerial, setManualSerial] = useState('');
  const [manualModel, setManualModel] = useState('');
  const [manualMac, setManualMac] = useState('');
  const [manualSubmitting, setManualSubmitting] = useState(false);

  useEffect(() => {
    fetchCustomerDetail();
    fetchAvailableDevices();
  }, [customerId]);

  useEffect(() => {
    const fetchTechnicians = async () => {
      try {
        setTechLoading(true);
        const token = localStorage.getItem("token");
        const res = await axios.get(`${BACKEND_URL}/api/technicians`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.data?.success) {
          setTechnicians(res.data.data || []);
        }
      } catch (err) {
        console.log("Failed to load technicians", err);
      } finally {
        setTechLoading(false);
      }
    };
    fetchTechnicians();
  }, []);

  useEffect(() => {
    if (activeTab === 'tickets') {
      fetchTickets();
    }
  }, [activeTab]);

  const fetchCustomerDetail = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      // Get customer basic info - use V2 API (MySQL)
      const customerRes = await axios.get(
        `${BACKEND_URL}/api/v2/customers/${customerId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setCustomer(customerRes.data.data);
      
      // 2026-07-15 NON-BLOCKING: fetch ACS status di background (fire-and-forget)
      // dengan timeout pendek. Kalau ACS server down, TIDAK block loading customer detail.
      if (customerRes.data.data?.wan_device_id) {
        const deviceId = customerRes.data.data.wan_device_id;
        if (!rebootingDevices[deviceId]) {
          axios.get(`${BACKEND_URL}/api/v2/acs/devices/status`, {
            headers: { Authorization: `Bearer ${token}` },
            timeout: 4000,  // hard limit 4s dari client-side
          })
            .then(acsRes => {
              if (acsRes.data?.success && acsRes.data.data) {
                const deviceStatus = acsRes.data.data[deviceId];
                if (deviceStatus) {
                  setAcsDeviceStatuses(prev => ({ ...prev, [deviceId]: deviceStatus }));
                }
              }
            })
            .catch(_acsErr => {
              // Silent — ACS optional. Customer detail sudah tampil.
              console.log('ACS status not available (backend circuit-breaker or timeout)');
            });
        }
      }
      
      // Get monitoring data (PPPoE + Devices) - try V2 first, fallback to legacy
      try {
        const monitoringRes = await axios.get(
          `${BACKEND_URL}/api/v2/customers/${customerId}/monitoring/detailed`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setMonitoringData(monitoringRes.data.data);
      } catch (monErr) {
        // Try legacy endpoint
        try {
          const monitoringRes = await axios.get(
            `${BACKEND_URL}/api/customers/${customerId}/monitoring/detailed`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          setMonitoringData(monitoringRes.data.data);
        } catch (legacyErr) {
          console.log('Monitoring data not available');
          setMonitoringData(null);
        }
      }
      
    } catch (error) {
      console.error('Error fetching customer detail:', error);
      toast.error('Failed to load customer details');
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableDevices = async () => {
    try {
      const token = localStorage.getItem('token');
      // 2026-06-02 FIX: use real ACS-managed WAN devices endpoint (not legacy /api/devices mock)
      const response = await axios.get(
        `${BACKEND_URL}/api/wan-devices/unlinked`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Normalize shape so existing JSX (device.id/model/serial_number/mac_address/status) keeps working
      const rows = (response.data?.data || []).map(d => ({
        id: d.device_id,
        model: d.model || '-',
        serial_number: d.serial_number || '-',
        mac_address: d.tr069_ip || d.external_ip || '',
        status: d.online ? 'online' : 'offline',
      }));
      setAvailableDevices(rows);
    } catch (error) {
      console.error('Error fetching unlinked wan-devices:', error);
    }
  };

  const fetchTickets = async () => {
    try {
      setTicketsLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${BACKEND_URL}/api/tickets?customer_id=${customerId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        setTickets(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching tickets:', error);
      toast.error('Gagal memuat tiket');
    } finally {
      setTicketsLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchCustomerDetail();
    setRefreshing(false);
    toast.success('Data refreshed');
  };

  const handleLinkDevice = async (deviceId) => {
    try {
      const token = localStorage.getItem('token');
      // 2026-06-02 FIX: use real WAN device link endpoint with body
      await axios.post(
        `${BACKEND_URL}/api/customers/${customerId}/link-wan-device`,
        { device_id: deviceId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Device linked successfully');
      setShowLinkDialog(false);
      await fetchCustomerDetail();
      await fetchAvailableDevices();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to link device');
    }
  };

  const handleLinkManual = async () => {
    if (!manualSerial.trim() || !manualModel.trim()) {
      toast.error('Serial dan Model wajib diisi');
      return;
    }
    try {
      setManualSubmitting(true);
      const token = localStorage.getItem('token');
      const resp = await axios.post(
        `${BACKEND_URL}/api/customers/${customerId}/link-wan-device/manual`,
        {
          serial: manualSerial.trim().toUpperCase(),
          model: manualModel.trim(),
          mac_address: manualMac.trim().toUpperCase(),
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(resp.data.message || 'Placeholder device terdaftar', { duration: 8000 });
      setShowManualForm(false);
      setManualSerial('');
      setManualModel('');
      setManualMac('');
      setShowLinkDialog(false);
      await fetchCustomerDetail();
      await fetchAvailableDevices();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal daftar device manual');
    } finally {
      setManualSubmitting(false);
    }
  };

  const handleUnlinkDevice = async (deviceId) => {
    if (!window.confirm('Unlink this device from customer?')) return;
    
    try {
      const token = localStorage.getItem('token');
      // 2026-06-02 FIX: use real WAN device unlink endpoint
      await axios.delete(
        `${BACKEND_URL}/api/customers/${customerId}/unlink-wan-device`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Device unlinked');
      await fetchCustomerDetail();
      await fetchAvailableDevices();
    } catch (error) {
      toast.error('Failed to unlink device');
    }
  };

  // 2026-06-04: Force delete device permanently from MongoDB (admin only).
  // Berguna saat ONT pelanggan diganti hardware atau sudah dihapus dari ACS,
  // dan admin ingin device hilang segera tanpa menunggu janitor 7 hari.
  const handleForceDeleteDevice = async (deviceId) => {
    const confirmed = window.confirm(
      'HAPUS PERMANEN device ini dari database?\n\n' +
      'Tindakan ini akan menghapus device secara total (bukan hanya unlink).\n' +
      'Gunakan ini jika ONT sudah diganti hardware atau sudah dihapus dari ACS.\n\n' +
      'Device akan auto re-create jika masih aktif di ACS.\n\n' +
      'Lanjutkan?'
    );
    if (!confirmed) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${BACKEND_URL}/api/wan-devices/${encodeURIComponent(deviceId)}/force`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Device deleted permanently');
      await fetchCustomerDetail();
      await fetchAvailableDevices();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete device');
    }
  };

  // Show reboot confirmation modal
  const showRebootConfirmation = (deviceId) => {
    setPendingRebootDeviceId(deviceId);
    setShowRebootConfirm(true);
  };

  // Execute reboot after confirmation
  const executeReboot = async () => {
    setShowRebootConfirm(false);
    if (pendingRebootDeviceId) {
      await handleDeviceAction(pendingRebootDeviceId, 'reboot', true);
    }
    setPendingRebootDeviceId(null);
  };

  const handleDeviceAction = async (deviceId, action, confirmed = false) => {
    // Show confirmation modal for reboot (unless already confirmed)
    if (action === 'reboot' && !confirmed) {
      showRebootConfirmation(deviceId);
      return;
    }
    
    // For reboot: Mark device as rebooting IMMEDIATELY before API call
    if (action === 'reboot') {
      setRebootingDevices(prev => ({ ...prev, [deviceId]: true }));
      // Also set ACS status to offline immediately for this device
      setAcsDeviceStatuses(prev => ({ 
        ...prev, 
        [deviceId]: { ...prev[deviceId], online: false } 
      }));
    }
    
    try {
      const token = localStorage.getItem('token');
      // 2026-06-02 FIX: route via /api/wan-devices/{id}/reboot (real ACS-backed endpoint)
      if (action === 'reboot') {
        await axios.post(
          `${BACKEND_URL}/api/wan-devices/${encodeURIComponent(deviceId)}/reboot`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } else {
        await axios.post(
          `${BACKEND_URL}/api/devices/${encodeURIComponent(deviceId)}/operations`,
          { operation: action },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }
      
      if (action === 'reboot') {
        toast.success('Reboot command sent! Device akan restart, status akan OFFLINE selama proses...');
        
        // After 2 minutes, remove this device from rebooting state
        setTimeout(() => {
          setRebootingDevices(prev => {
            const newState = { ...prev };
            delete newState[deviceId];
            return newState;
          });
          toast.info('Checking device status...');
          fetchCustomerDetail();
        }, 120000);
        
        // Additional check at 3 minutes
        setTimeout(() => fetchCustomerDetail(), 180000);
      } else {
        toast.success(`${action} command sent`);
        setTimeout(() => fetchCustomerDetail(), 5000);
      }
    } catch (error) {
      console.error('Device action error:', error);
      // If reboot failed, revert the rebooting state
      if (action === 'reboot') {
        setRebootingDevices(prev => {
          const newState = { ...prev };
          delete newState[deviceId];
          return newState;
        });
      }
      toast.error(`Failed to ${action} device: ${error.response?.data?.detail || error.message}`);
    }
  };

  // Helper to check if a device is rebooting
  const isDeviceRebooting = (deviceId) => {
    return rebootingDevices[deviceId] === true;
  };

  // Helper to get device online status (considers rebooting state)
  const getDeviceOnlineStatus = (deviceId) => {
    if (isDeviceRebooting(deviceId)) {
      return false; // Always show offline if rebooting
    }
    return acsDeviceStatuses[deviceId]?.online || false;
  };

  // Open WiFi settings modal
  const openWifiSettings = (deviceId) => {
    setSelectedDeviceForWifi(deviceId);
    setShowWifiSettings(true);
  };


  // Build the customer info message used by both Copy and "Send to Teknisi" features
  const buildCustomerMessage = (technicianName) => {
    if (!customer) return '';
    const greet = technicianName
      ? `Halo *${technicianName}*, mohon bantu cek pelanggan berikut:\n`
      : '';
    const lines = [
      greet,
      `📋 *Info Pelanggan*`,
      `Nama: ${customer.name || '-'}`,
      customer.account_number ? `No. Akun: ${customer.account_number}` : null,
      `Phone: ${customer.phone || '-'}`,
      customer.email ? `Email: ${customer.email}` : null,
      `Alamat: ${customer.address || '-'}`,
      customer.service_plan_name ? `Paket: ${customer.service_plan_name}` : null,
      customer.installation_date
        ? `Tgl Pasang: ${new Date(customer.installation_date).toLocaleDateString('id-ID')}`
        : null,
      customer.current_ip ? `IP: ${customer.current_ip}` : null,
      customer.branch_name ? `Cabang: ${customer.branch_name}` : null,
      `\n🔐 *Kredensial PPPoE*`,
      `Username: ${customer.pppoe_username || '-'}`,
      `Password: ${customer.pppoe_password || '-'}`,
    ].filter(Boolean);
    return lines.join('\n');
  };

  // Helpers for sending message to CUSTOMER via WhatsApp
  const formatRupiah = (n) => {
    if (n === null || n === undefined || n === '') return '-';
    const num = Number(n);
    if (isNaN(num)) return String(n);
    return 'Rp ' + num.toLocaleString('id-ID');
  };
  const formatDateID = (d) => {
    if (!d) return '-';
    try { return new Date(d).toLocaleDateString('id-ID', { day: '2-digit', month: 'long', year: 'numeric' }); }
    catch { return String(d); }
  };
  const dueStatusLabel = () => {
    if (!customer?.next_due_date) return '';
    const today = new Date(); today.setHours(0,0,0,0);
    const due = new Date(customer.next_due_date); due.setHours(0,0,0,0);
    const diff = Math.round((due - today) / (1000 * 60 * 60 * 24));
    if (diff > 0) return ` (H-${diff})`;
    if (diff === 0) return ' (Jatuh Tempo Hari Ini)';
    return ` (Telat ${Math.abs(diff)} hari)`;
  };
  const buildReminderMessage = () => {
    if (!customer) return '';
    return [
      `Halo Bapak/Ibu *${customer.name || 'Pelanggan'}* 🙏`,
      ``,
      `Kami informasikan tagihan internet Anda:`,
      `📦 Paket: ${customer.service_plan_name || '-'}`,
      `💰 Tagihan: *${formatRupiah(customer.monthly_price)}*`,
      `📅 Jatuh Tempo: *${formatDateID(customer.next_due_date)}*${dueStatusLabel()}`,
      customer.account_number ? `🆔 No. Akun: ${customer.account_number}` : null,
      ``,
      `Mohon segera melakukan pembayaran sebelum jatuh tempo agar layanan internet Anda tetap aktif.`,
      ``,
      `Terima kasih,`,
      `Tim ${customer.branch_name || 'Customer Service'}`,
    ].filter((x) => x !== null).join('\n');
  };
  const buildPaidConfirmMessage = () => {
    if (!customer) return '';
    return [
      `Halo Bapak/Ibu *${customer.name || 'Pelanggan'}* 🙏`,
      ``,
      `Terima kasih, pembayaran tagihan internet Anda telah kami terima ✅`,
      ``,
      `📦 Paket: ${customer.service_plan_name || '-'}`,
      `💰 Jumlah: *${formatRupiah(customer.monthly_price)}*`,
      `📅 Periode Berikutnya: *${formatDateID(customer.next_due_date)}*`,
      customer.account_number ? `🆔 No. Akun: ${customer.account_number}` : null,
      ``,
      `Status layanan: *AKTIF (LUNAS)*`,
      ``,
      `Selamat menikmati layanan kami 🚀`,
      ``,
      `Salam,`,
      `Tim ${customer.branch_name || 'Customer Service'}`,
    ].filter((x) => x !== null).join('\n');
  };
  const buildAccountInfoMessage = () => {
    if (!customer) return '';
    const statusLabel = (customer.payment_status === 'lunas') ? 'LUNAS ✅' :
      (customer.payment_status === 'isolir') ? 'ISOLIR ⛔' : 'BELUM BAYAR ⚠️';
    return [
      `Halo Bapak/Ibu *${customer.name || 'Pelanggan'}* 🙏`,
      ``,
      `Berikut informasi akun internet Anda:`,
      ``,
      `📦 Paket: ${customer.service_plan_name || '-'}`,
      `💰 Biaya Bulanan: *${formatRupiah(customer.monthly_price)}*`,
      customer.account_number ? `🆔 No. Akun: ${customer.account_number}` : null,
      `📅 Jatuh Tempo: ${formatDateID(customer.next_due_date)}${dueStatusLabel()}`,
      `📊 Status: *${statusLabel}*`,
      customer.address ? `🏠 Alamat: ${customer.address}` : null,
      ``,
      `Jika ada pertanyaan, silakan balas pesan ini.`,
      ``,
      `Terima kasih,`,
      `Tim ${customer.branch_name || 'Customer Service'}`,
    ].filter((x) => x !== null).join('\n');
  };
  const sendMessageToCustomer = (messageText, templateLabel) => {
    if (!customer) return;
    const rawPhone = (customer.phone || '').replace(/[^0-9]/g, '');
    if (!rawPhone) {
      toast.error('No. HP pelanggan kosong / tidak valid');
      return;
    }
    let waPhone = rawPhone;
    if (waPhone.startsWith('62')) {
      // already international
    } else if (waPhone.startsWith('0')) {
      waPhone = '62' + waPhone.slice(1);
    } else if (waPhone.startsWith('8')) {
      waPhone = '62' + waPhone;
    }
    const text = encodeURIComponent(messageText);
    window.open(`https://wa.me/${waPhone}?text=${text}`, '_blank');
    setShowCustomerMsgDropdown(false);
    toast.success(`Membuka WhatsApp ke ${customer.name || 'pelanggan'} (${templateLabel})`);
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-indigo-600 border-t-transparent"></div>
          <p className="mt-4 text-gray-600 dark:text-slate-300">Loading customer details...</p>
        </div>
      </div>
    );
  }

  if (!customer) {
    return (
      <div className="p-6">
        <div className="text-center py-12">
          <AlertCircle className="mx-auto text-red-500 mb-4" size={48} />
          <h2 className="text-xl font-semibold text-gray-900 mb-2 dark:text-slate-100">Customer Not Found</h2>
          <button
            onClick={() => navigate('/app/customers')}
            className="mt-4 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
          >
            Back to Customers
          </button>
        </div>
      </div>
    );
  }

  // Get devices from monitoring data, or create a device entry from wan_device_id
  const devices = monitoringData?.devices?.length > 0 
    ? monitoringData.devices 
    : (customer?.wan_device_id ? [{
        id: customer.wan_device_id,
        device_id: customer.wan_device_id,
        serial_number: customer.wan_device_id.split('-').pop() || customer.wan_device_id,
        status: 'linked'
      }] : []);
  
  // Use customer.is_online directly since it's already available from the API
  const pppoeStatus = monitoringData?.pppoe_status || {
    online: customer?.is_online === 1 || customer?.is_online === true,
    ip_address: customer?.current_ip || customer?.last_ip_address,
    uptime: monitoringData?.uptime || null
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Reboot Confirmation Modal */}
      <ConfirmModal
        isOpen={showRebootConfirm}
        onClose={() => setShowRebootConfirm(false)}
        onConfirm={executeReboot}
        title="Konfirmasi Reboot"
        message="Anda yakin ingin me-reboot device ini?\n\nDevice akan offline selama 1-2 menit.\nPelanggan akan kehilangan koneksi internet sementara."
      />

      {/* WiFi Settings Modal */}
      <WiFiSettingsModal
        isOpen={showWifiSettings}
        onClose={() => {
          setShowWifiSettings(false);
          setSelectedDeviceForWifi(null);
        }}
        deviceId={selectedDeviceForWifi}
      />
      
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/app/customers')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4 dark:text-slate-300"
        >
          <ArrowLeft size={20} />
          Back to Customers
        </button>
        
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="min-w-0">
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900 truncate dark:text-slate-100">{customer.name}</h1>
            <p className="text-xs sm:text-sm text-gray-500 mt-1 dark:text-slate-400">Customer ID: {customer.account_number || customer.id}</p>
          </div>
          
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              onClick={() => setShowCreateInvoice(true)}
              className="flex items-center justify-center gap-1.5 px-3 sm:px-4 py-2 bg-indigo-600 text-white text-sm rounded-lg hover:bg-indigo-700 transition-colors flex-1 sm:flex-none whitespace-nowrap"
              data-testid="btn-create-invoice-customer"
            >
              <Receipt size={16} className="flex-shrink-0" />
              <span>Buat Tagihan</span>
            </button>
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className="flex items-center justify-center gap-1.5 px-3 sm:px-4 py-2 border border-gray-300 text-sm rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 flex-shrink-0 dark:border-slate-600 dark:hover:bg-slate-700/50"
              aria-label="Refresh"
            >
              <RefreshCw size={16} className={refreshing ? 'animate-spin' : ''} />
              <span className="hidden sm:inline">Refresh</span>
            </button>
          </div>
        </div>
      </div>

      <CreateInvoiceModal
        isOpen={showCreateInvoice}
        onClose={() => setShowCreateInvoice(false)}
        prefilledCustomer={customer}
        onCreated={() => { setShowCreateInvoice(false); }}
      />

      {/* Tabs */}
      <div className="mb-6 border-b border-gray-200 -mx-3 sm:mx-0 dark:border-slate-700">
        <nav className="flex gap-2 sm:gap-6 overflow-x-auto px-3 sm:px-0 scrollbar-hide">
          <button
            onClick={() => setActiveTab('overview')}
            className={`pb-3 sm:pb-4 px-2 sm:px-1 border-b-2 font-medium text-xs sm:text-sm transition-colors whitespace-nowrap flex items-center gap-1.5 ${
              activeTab === 'overview'
                ? 'border-indigo-600 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <User size={14} className="flex-shrink-0" />
            Overview
          </button>
          
          <button
            onClick={() => setActiveTab('connection')}
            className={`pb-3 sm:pb-4 px-2 sm:px-1 border-b-2 font-medium text-xs sm:text-sm transition-colors whitespace-nowrap flex items-center gap-1.5 ${
              activeTab === 'connection'
                ? 'border-indigo-600 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <Activity size={14} className="flex-shrink-0" />
            <span className="hidden sm:inline">Device & Connection</span>
            <span className="sm:hidden">Device</span>
            {devices.length > 0 && (
              <span className="ml-1 px-1.5 py-0.5 bg-indigo-100 text-indigo-600 rounded-full text-[10px] sm:text-xs dark:bg-indigo-900/40 dark:text-indigo-400">
                {devices.length}
              </span>
            )}
          </button>

          <button
            onClick={() => setActiveTab('tickets')}
            className={`pb-3 sm:pb-4 px-2 sm:px-1 border-b-2 font-medium text-xs sm:text-sm transition-colors whitespace-nowrap flex items-center gap-1.5 ${
              activeTab === 'tickets'
                ? 'border-indigo-600 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <Ticket size={14} className="flex-shrink-0" />
            Tickets
            {tickets.length > 0 && (
              <span className="ml-1 px-1.5 py-0.5 bg-indigo-100 text-indigo-600 rounded-full text-[10px] sm:text-xs dark:bg-indigo-900/40 dark:text-indigo-400">
                {tickets.length}
              </span>
            )}
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Customer Info Card */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 dark:bg-slate-800 dark:border-slate-700">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-slate-100">Customer Information</h2>
              <div className="grid grid-cols-2 sm:flex sm:flex-wrap items-center gap-2 relative w-full sm:w-auto">
                <button
                  type="button"
                  onClick={() => {
                    navigator.clipboard.writeText(buildCustomerMessage());
                    toast.success('Info pelanggan tersalin');
                  }}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-blue-600 hover:text-white hover:bg-blue-600 border border-blue-200 hover:border-blue-600 rounded-lg transition-all whitespace-nowrap justify-center sm:min-w-[9rem] flex-1 sm:flex-none dark:text-blue-400"
                  title="Salin info pelanggan untuk dibagikan ke teknisi lapangan"
                  data-testid="copy-all-customer-info-btn"
                >
                  <Copy size={13} /> Copy
                </button>
                <button
                  type="button"
                  onClick={() => setShowTechDropdown((v) => !v)}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-emerald-600 hover:text-white hover:bg-emerald-600 border border-emerald-200 hover:border-emerald-600 rounded-lg transition-all whitespace-nowrap justify-center sm:min-w-[9rem] flex-1 sm:flex-none dark:text-emerald-400"
                  title="Kirim info pelanggan ke teknisi via WhatsApp"
                  data-testid="send-to-technician-btn"
                >
                  <MessageCircle size={13} /> Kirim ke Teknisi
                </button>
                <button
                  type="button"
                  onClick={() => setShowCustomerMsgDropdown((v) => !v)}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-sky-600 hover:text-white hover:bg-sky-600 border border-sky-200 hover:border-sky-600 rounded-lg transition-all whitespace-nowrap justify-center sm:min-w-[9rem] flex-1 sm:flex-none col-span-2 sm:col-span-1"
                  title="Kirim pesan ke pelanggan via WhatsApp"
                  data-testid="send-to-customer-btn"
                >
                  <Send size={13} /> Kirim ke Pelanggan
                </button>
                {showCustomerMsgDropdown && (
                  <>
                    <div
                      className="fixed inset-0 z-20"
                      onClick={() => setShowCustomerMsgDropdown(false)}
                      data-testid="customer-msg-overlay"
                    />
                    <div
                      className="absolute right-0 top-full mt-2 w-72 bg-white rounded-xl shadow-2xl border border-gray-200 z-30 overflow-hidden dark:bg-slate-800 dark:border-slate-700"
                      data-testid="customer-msg-dropdown"
                    >
                      <div className="px-3 py-2 bg-gray-50 border-b border-gray-200 dark:bg-slate-900 dark:border-slate-700">
                        <p className="text-xs font-semibold text-gray-700 dark:text-slate-200">Pilih Template Pesan</p>
                        <p className="text-[10px] text-gray-500 truncate dark:text-slate-400">Akan dikirim ke {customer?.phone || '-'}</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => sendMessageToCustomer(buildReminderMessage(), 'Reminder Tagihan')}
                        className="w-full text-left px-3 py-2.5 hover:bg-orange-50 border-b border-gray-100 transition-colors flex items-start gap-2 dark:hover:bg-orange-900/30 dark:border-slate-700"
                        data-testid="tpl-reminder-btn"
                      >
                        <span className="text-base leading-none mt-0.5">⏰</span>
                        <div className="min-w-0 flex-1">
                          <p className="text-xs font-semibold text-gray-900 dark:text-slate-100">Reminder Tagihan</p>
                          <p className="text-[10px] text-gray-500 dark:text-slate-400">Pengingat jatuh tempo bulan ini</p>
                        </div>
                      </button>
                      <button
                        type="button"
                        onClick={() => sendMessageToCustomer(buildPaidConfirmMessage(), 'Konfirmasi Lunas')}
                        className="w-full text-left px-3 py-2.5 hover:bg-emerald-50 border-b border-gray-100 transition-colors flex items-start gap-2 dark:hover:bg-emerald-900/30 dark:border-slate-700"
                        data-testid="tpl-paid-btn"
                      >
                        <span className="text-base leading-none mt-0.5">✅</span>
                        <div className="min-w-0 flex-1">
                          <p className="text-xs font-semibold text-gray-900 dark:text-slate-100">Konfirmasi Pembayaran</p>
                          <p className="text-[10px] text-gray-500 dark:text-slate-400">Ucapan terima kasih, status LUNAS</p>
                        </div>
                      </button>
                      <button
                        type="button"
                        onClick={() => sendMessageToCustomer(buildAccountInfoMessage(), 'Info Akun')}
                        className="w-full text-left px-3 py-2.5 hover:bg-sky-50 transition-colors flex items-start gap-2"
                        data-testid="tpl-account-info-btn"
                      >
                        <span className="text-base leading-none mt-0.5">📊</span>
                        <div className="min-w-0 flex-1">
                          <p className="text-xs font-semibold text-gray-900 dark:text-slate-100">Info Akun & Paket</p>
                          <p className="text-[10px] text-gray-500 dark:text-slate-400">Detail paket, status, jatuh tempo</p>
                        </div>
                      </button>
                    </div>
                  </>
                )}
                {showTechDropdown && (
                  <>
                    <div
                      className="fixed inset-0 z-20"
                      onClick={() => setShowTechDropdown(false)}
                      data-testid="tech-dropdown-overlay"
                    />
                    <div
                      className="absolute right-0 top-full mt-2 w-72 bg-white rounded-xl shadow-2xl border border-gray-200 z-30 overflow-hidden dark:bg-slate-800 dark:border-slate-700"
                      data-testid="tech-dropdown"
                    >
                      <div className="px-3 py-2 bg-gray-50 border-b border-gray-200 dark:bg-slate-900 dark:border-slate-700">
                        <p className="text-xs font-semibold text-gray-700 dark:text-slate-200">Pilih Teknisi</p>
                        <p className="text-[10px] text-gray-500 dark:text-slate-400">Pesan akan terbuka di WhatsApp</p>
                      </div>
                      <div className="px-2 py-2 border-b border-gray-100 dark:border-slate-700">
                        <input
                          type="text"
                          value={techSearch}
                          onChange={(e) => setTechSearch(e.target.value)}
                          placeholder="Cari teknisi..."
                          className="w-full text-xs px-2 py-1.5 border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-emerald-500 dark:border-slate-700"
                          data-testid="tech-search-input"
                        />
                      </div>
                      <div className="max-h-64 overflow-y-auto">
                        {techLoading ? (
                          <div className="px-3 py-4 text-center text-xs text-gray-500 dark:text-slate-400">Memuat teknisi...</div>
                        ) : technicians.length === 0 ? (
                          <div className="px-3 py-4 text-center text-xs text-gray-500 dark:text-slate-400">
                            Belum ada teknisi terdaftar.<br />
                            <span className="text-[10px]">Tambahkan user dengan role 'teknisi' dan No. HP.</span>
                          </div>
                        ) : (
                          technicians
                            .filter((t) => {
                              if (!techSearch) return true;
                              const q = techSearch.toLowerCase();
                              return (
                                (t.full_name || '').toLowerCase().includes(q) ||
                                (t.phone || '').includes(q)
                              );
                            })
                            .map((t) => (
                              <button
                                key={t.id}
                                type="button"
                                onClick={() => {
                                  const phone = (t.phone || '').replace(/[^0-9]/g, '');
                                  if (!phone) {
                                    toast.error(`No. HP teknisi ${t.full_name} tidak valid`);
                                    return;
                                  }
                                  // Normalize: 08xxx -> 628xxx, 8xxx -> 628xxx
                                  let waPhone = phone;
                                  if (waPhone.startsWith('62')) {
                                    // already international
                                  } else if (waPhone.startsWith('0')) {
                                    waPhone = '62' + waPhone.slice(1);
                                  } else if (waPhone.startsWith('8')) {
                                    waPhone = '62' + waPhone;
                                  }
                                  const text = encodeURIComponent(buildCustomerMessage(t.full_name));
                                  window.open(`https://wa.me/${waPhone}?text=${text}`, '_blank');
                                  setShowTechDropdown(false);
                                  setTechSearch('');
                                  toast.success(`Membuka WhatsApp ke ${t.full_name}`);
                                }}
                                className="w-full text-left px-3 py-2 hover:bg-emerald-50 border-b border-gray-50 transition-colors flex items-center justify-between gap-2 dark:hover:bg-emerald-900/30"
                                data-testid={`tech-item-${t.id}`}
                              >
                                <div className="min-w-0 flex-1">
                                  <p className="text-xs font-semibold text-gray-900 truncate dark:text-slate-100">{t.full_name || t.username}</p>
                                  <p className="text-[10px] text-gray-500 dark:text-slate-400">{t.phone}</p>
                                </div>
                                <Send size={12} className="text-emerald-600 flex-shrink-0 dark:text-emerald-400" />
                              </button>
                            ))
                        )}
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
            
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <Phone className="text-gray-400 mt-1" size={18} />
                <div>
                  <p className="text-sm text-gray-500 dark:text-slate-400">Phone</p>
                  <p className="text-sm font-medium text-gray-900 dark:text-slate-100">{customer.phone || '-'}</p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <Mail className="text-gray-400 mt-1" size={18} />
                <div>
                  <p className="text-sm text-gray-500 dark:text-slate-400">Email</p>
                  <p className="text-sm font-medium text-gray-900 dark:text-slate-100">{customer.email || '-'}</p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <MapPin className="text-gray-400 mt-1" size={18} />
                <div>
                  <p className="text-sm text-gray-500 dark:text-slate-400">Address</p>
                  <p className="text-sm font-medium text-gray-900 dark:text-slate-100">{customer.address || '-'}</p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <Package className="text-gray-400 mt-1" size={18} />
                <div>
                  <p className="text-sm text-gray-500 dark:text-slate-400">Service Package</p>
                  <p className="text-sm font-medium text-gray-900 dark:text-slate-100">{customer.service_plan_name || customer.package_name || customer.service_plan || '-'}</p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <Calendar className="text-gray-400 mt-1" size={18} />
                <div>
                  <p className="text-sm text-gray-500 dark:text-slate-400">Registration Date</p>
                  <p className="text-sm font-medium text-gray-900 dark:text-slate-100">
                    {customer.installation_date ? new Date(customer.installation_date).toLocaleDateString('id-ID') : '-'}
                  </p>
                </div>
              </div>

              {/* PPPoE Credentials */}
              {(customer.pppoe_username || customer.pppoe_password) && (
                <>
                  <div className="flex items-start gap-3" data-testid="pppoe-username-row">
                    <Wifi className="text-blue-500 mt-1" size={18} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-500 dark:text-slate-400">PPPoE Username</p>
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-mono font-medium text-gray-900 truncate dark:text-slate-100">
                          {customer.pppoe_username || '-'}
                        </p>
                        {customer.pppoe_username && (
                          <button type="button" onClick={() => { navigator.clipboard.writeText(customer.pppoe_username); toast.success('Username disalin'); }}
                            className="text-xs text-blue-600 hover:text-blue-800 flex-shrink-0 dark:text-blue-400" title="Salin" data-testid="pppoe-username-copy-btn"><Copy size={13} /></button>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3" data-testid="pppoe-password-row">
                    <Lock className="text-amber-500 mt-1" size={18} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-500 dark:text-slate-400">PPPoE Password</p>
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-mono font-medium text-gray-900 truncate dark:text-slate-100">
                          {customer.pppoe_password ? (showPasswordCustomer ? customer.pppoe_password : '••••••••••') : '-'}
                        </p>
                        {customer.pppoe_password && (
                          <>
                            <button type="button" onClick={() => setShowPasswordCustomer(!showPasswordCustomer)}
                              className="text-xs text-gray-500 hover:text-gray-800 flex-shrink-0 dark:text-slate-400" title={showPasswordCustomer ? 'Sembunyikan' : 'Tampilkan'} data-testid="pppoe-password-toggle-btn">
                              {showPasswordCustomer ? <EyeOff size={14} /> : <Eye size={14} />}
                            </button>
                            <button type="button" onClick={() => { navigator.clipboard.writeText(customer.pppoe_password); toast.success('Password disalin'); }}
                              className="text-xs text-blue-600 hover:text-blue-800 flex-shrink-0 dark:text-blue-400" title="Salin" data-testid="pppoe-password-copy-btn"><Copy size={13} /></button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
            
            {/* Notification Status */}
            {customer.notification_status && (
              <div className="mt-4 pt-4 border-t border-gray-200 dark:border-slate-700">
                <h3 className="text-sm font-semibold text-gray-700 mb-3 dark:text-slate-200">Status Notifikasi</h3>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600 dark:text-slate-300">WhatsApp</span>
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      customer.notification_status.wa_status === 'sent' 
                        ? 'bg-green-100 text-green-700' 
                        : customer.notification_status.wa_status === 'pending'
                        ? 'bg-yellow-100 text-yellow-700'
                        : 'bg-red-100 text-red-700'
                    }`}>
                      {customer.notification_status.wa_status === 'sent' ? '✓ Terkirim' : 
                       customer.notification_status.wa_status === 'pending' ? '⏳ Pending' : '✗ Gagal'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600 dark:text-slate-300">Email</span>
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      customer.notification_status.email_status === 'sent' 
                        ? 'bg-green-100 text-green-700' 
                        : customer.notification_status.email_status === 'pending'
                        ? 'bg-yellow-100 text-yellow-700'
                        : 'bg-red-100 text-red-700'
                    }`}>
                      {customer.notification_status.email_status === 'sent' ? '✓ Terkirim' : 
                       customer.notification_status.email_status === 'pending' ? '⏳ Pending' : '✗ Gagal'}
                    </span>
                  </div>
                  {customer.notification_status.wa_error && (
                    <p className="text-xs text-red-600 dark:text-red-400">WA Error: {customer.notification_status.wa_error}</p>
                  )}
                  {customer.notification_status.email_error && (
                    <p className="text-xs text-red-600 dark:text-red-400">Email Error: {customer.notification_status.email_error}</p>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Quick Stats */}
          <div className="space-y-4">
            {/* PPPoE Status */}
            {customer.mikrotik_user && (
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 dark:bg-slate-800 dark:border-slate-700">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 dark:text-slate-100">PPPoE Connection</h3>
                
                {/* Status Badge */}
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    {pppoeStatus?.online ? (
                      <Wifi className="text-green-600 dark:text-green-400" size={24} />
                    ) : (
                      <WifiOff className="text-gray-400" size={24} />
                    )}
                    <div>
                      <p className="text-sm font-medium text-gray-900 dark:text-slate-100">
                        {customer.mikrotik_user}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-slate-400">
                        {pppoeStatus?.online ? 'Connected' : 'Disconnected'}
                      </p>
                    </div>
                  </div>
                  <span className={`px-3 py-1 rounded-md text-xs font-medium ${
                    pppoeStatus?.online 
                      ? 'bg-green-50 text-green-700 border border-green-200' 
                      : 'bg-gray-50 text-gray-700 border border-gray-200'
                  }`}>
                    {pppoeStatus?.online ? 'Online' : 'Offline'}
                  </span>
                </div>

                {/* PPPoE Credentials */}
                <div className="pt-4 border-t border-gray-200 space-y-3 dark:border-slate-700">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-500 dark:text-slate-400">Secret (Username)</span>
                    <span className="text-xs font-mono font-medium text-gray-900 bg-gray-50 px-2 py-1 rounded dark:text-slate-100 dark:bg-slate-900">
                      {customer.mikrotik_user}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-500 dark:text-slate-400">Password</span>
                    <span className="text-xs font-mono font-medium text-gray-900 bg-gray-50 px-2 py-1 rounded dark:text-slate-100 dark:bg-slate-900">
                      {customer.mikrotik_password || '********'}
                    </span>
                  </div>
                  {pppoeStatus?.ip_address && (
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-500 dark:text-slate-400">IP Address</span>
                      <span className="text-xs font-mono font-medium text-gray-900 bg-gray-50 px-2 py-1 rounded dark:text-slate-100 dark:bg-slate-900">
                        {pppoeStatus.ip_address}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Devices Summary */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 dark:bg-slate-800 dark:border-slate-700">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 dark:text-slate-100">CPE Devices</h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600 dark:text-slate-300">Total Devices</span>
                  <span className="text-lg font-bold text-gray-900 dark:text-slate-100">{devices.length}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600 dark:text-slate-300">Online</span>
                  <span className="text-lg font-bold text-green-600 dark:text-green-400">
                    {pppoeStatus?.online ? 1 : devices.filter(d => d.status === 'online').length}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600 dark:text-slate-300">Offline</span>
                  <span className="text-lg font-bold text-gray-600 dark:text-slate-300">
                    {!pppoeStatus?.online ? 1 : devices.filter(d => d.status !== 'online').length}
                  </span>
                </div>
                {pppoeStatus?.ip_address && (
                  <div className="flex items-center justify-between pt-2 border-t border-gray-100 dark:border-slate-700">
                    <span className="text-sm text-gray-600 dark:text-slate-300">IP Address</span>
                    <span className="text-sm font-mono font-medium text-indigo-600 dark:text-indigo-400">
                      {pppoeStatus.ip_address}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'overview' && (
        <div className="mt-6">
          <CustomerLocationCard customer={customer} />
        </div>
      )}

      {activeTab === 'connection' && (
        <div className="space-y-6">
          {/* Actions Bar */}
          <div className="flex items-center justify-between gap-3">
            <h2 className="text-base sm:text-lg font-semibold text-gray-900 min-w-0 truncate dark:text-slate-100">
              CPE Devices ({devices.length})
            </h2>
            <button
              onClick={() => setShowLinkDialog(true)}
              className="flex items-center justify-center gap-1.5 px-3 sm:px-4 py-2 border border-indigo-300 text-indigo-600 text-sm rounded-lg hover:bg-indigo-50 transition-colors font-medium whitespace-nowrap flex-shrink-0 dark:text-indigo-400 dark:hover:bg-indigo-900/30"
            >
              <LinkIcon size={16} />
              <span className="hidden xs:inline sm:inline">Link Device</span>
              <span className="xs:hidden sm:hidden">Link</span>
            </button>
          </div>

          {/* Devices List */}
          {devices.length === 0 ? (
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-12 text-center dark:bg-slate-800 dark:border-slate-700">
              <Router className="mx-auto text-gray-300 mb-4" size={48} />
              <h3 className="text-lg font-medium text-gray-900 mb-2 dark:text-slate-100">No Devices Linked</h3>
              <p className="text-sm text-gray-500 mb-4 dark:text-slate-400">
                Link a CPE device to monitor connection and perform remote management
              </p>
              <button
                onClick={() => setShowLinkDialog(true)}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
              >
                Link Device
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-4">
              {devices.map((device) => (
                <div key={device.id} className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 sm:p-6 dark:bg-slate-800 dark:border-slate-700">
                  {/* Device Header - Responsive */}
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-4">
                    {/* Device Info */}
                    <div className="flex items-start gap-3">
                      <div className="p-2 sm:p-3 bg-indigo-50 rounded-lg shrink-0 dark:bg-indigo-900/30">
                        <Router className="text-indigo-600 dark:text-indigo-400" size={20} />
                      </div>
                      <div className="min-w-0">
                        <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-slate-100">{device.model || 'ONT Device'}</h3>
                        <p className="text-xs sm:text-sm text-gray-500 truncate dark:text-slate-400">ID: {device.serial_number || device.id}</p>
                      </div>
                    </div>
                    
                    {/* Status Badges - Stack on mobile */}
                    <div className="flex items-center gap-2 flex-wrap">
                      {/* ACS Status from database */}
                      {(() => {
                        const acsStatus = customer?.acs_status || 'offline';
                        const lastInform = customer?.acs_last_inform;
                        let timeAgo = '';
                        if (lastInform) {
                          const diff = Math.floor((new Date() - new Date(lastInform)) / 60000);
                          if (diff < 1) timeAgo = 'just now';
                          else if (diff < 60) timeAgo = diff + 'm ago';
                          else if (diff < 1440) timeAgo = Math.floor(diff/60) + 'h ago';
                          else timeAgo = Math.floor(diff/1440) + 'd ago';
                        }
                        const cfg = {
                          online: { bg: 'bg-green-50 text-green-600 border-green-300', dot: 'bg-green-500 animate-pulse', label: 'Online' },
                          away: { bg: 'bg-yellow-50 text-yellow-600 border-yellow-300', dot: 'bg-yellow-500', label: 'Away' },
                          offline: { bg: 'bg-slate-50 text-slate-500 border-slate-300', dot: 'bg-slate-400', label: 'Offline' }
                        }[acsStatus] || { bg: 'bg-slate-50 text-slate-500 border-slate-300', dot: 'bg-slate-400', label: 'Offline' };
                        return (
                          <span className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium border ${cfg.bg}`} title={timeAgo}>
                            <span className={`w-1.5 h-1.5 rounded-full mr-1.5 ${cfg.dot}`}></span>
                            {isDeviceRebooting(device.id) ? 'Rebooting...' : cfg.label}
                          </span>
                        );
                      })()}
                      
                      {/* Signal ONT from database */}
                      {customer?.ont_rx_power !== null && customer?.ont_rx_power !== undefined && customer?.acs_status === "online" && (
                        <span className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium border ${
                          customer.ont_signal_status === 'good' 
                            ? 'bg-green-50 text-green-600 border-green-300' 
                            : customer.ont_signal_status === 'warning'
                            ? 'bg-yellow-50 text-yellow-600 border-yellow-300'
                            : 'bg-red-50 text-red-600 border-red-300'
                        }`}>
                          <span className={`w-1.5 h-1.5 rounded-full mr-1.5 ${
                            customer.ont_signal_status === 'good' ? 'bg-green-500' : 
                            customer.ont_signal_status === 'warning' ? 'bg-yellow-500' : 'bg-red-500'
                          }`}></span>
                          {customer.ont_rx_power?.toFixed(1)} dBm
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Device Stats */}
                  {device.stats && (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4 p-4 bg-gray-50 rounded-lg dark:bg-slate-900">
                      <div>
                        <p className="text-xs text-gray-500 mb-1 dark:text-slate-400">Download</p>
                        <p className="text-sm font-semibold text-gray-900 flex items-center gap-1 dark:text-slate-100">
                          <TrendingDown size={14} className="text-green-600 dark:text-green-400" />
                          {device.stats.bandwidth?.download_mbps || 0} Mbps
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 mb-1 dark:text-slate-400">Upload</p>
                        <p className="text-sm font-semibold text-gray-900 flex items-center gap-1 dark:text-slate-100">
                          <TrendingUp size={14} className="text-blue-600 dark:text-blue-400" />
                          {device.stats.bandwidth?.upload_mbps || 0} Mbps
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 mb-1 dark:text-slate-400">Signal</p>
                        <p className="text-sm font-semibold text-gray-900 flex items-center gap-1 dark:text-slate-100">
                          <Signal size={14} className="text-indigo-600 dark:text-indigo-400" />
                          {device.stats.network?.signal_strength || '-'}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 mb-1 dark:text-slate-400">Clients</p>
                        <p className="text-sm font-semibold text-gray-900 flex items-center gap-1 dark:text-slate-100">
                          <User size={14} className="text-gray-600 dark:text-slate-300" />
                          {device.stats.network?.connected_clients || 0}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="grid grid-cols-2 sm:flex sm:items-center gap-2 sm:flex-wrap mt-3">
                    <button
                      onClick={() => handleDeviceAction(device.id, 'reboot')}
                      disabled={isDeviceRebooting(device.id)}
                      className={`flex items-center justify-center gap-1.5 px-3 py-2 text-xs sm:text-sm border rounded-lg transition-colors font-medium ${
                        isDeviceRebooting(device.id)
                          ? 'border-gray-200 bg-gray-100 text-gray-400 cursor-not-allowed' 
                          : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      <Power size={14} className={isDeviceRebooting(device.id) ? 'animate-spin' : ''} />
                      <span>{isDeviceRebooting(device.id) ? 'Rebooting...' : 'Reboot'}</span>
                    </button>
                    
                    <button
                      onClick={() => {
                        // Navigate to WAN devices page with device filter
                        navigate(`/app/wan-devices?device=${encodeURIComponent(device.id)}`);
                      }}
                      className="flex items-center justify-center gap-1.5 px-3 py-2 text-xs sm:text-sm border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-700/50"
                    >
                      <Settings size={14} />
                      Configure
                    </button>

                    {/* WiFi Settings Button - outline like Reboot/Configure */}
                    <button
                      onClick={() => openWifiSettings(device.id)}
                      className="flex items-center justify-center gap-1.5 px-3 py-2 text-xs sm:text-sm border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-700/50"
                    >
                      <Wifi size={14} />
                      <span className="hidden sm:inline">WiFi Settings</span>
                      <span className="sm:hidden">WiFi</span>
                    </button>
                    
                    <button
                      onClick={() => handleUnlinkDevice(device.id)}
                      className="flex items-center justify-center gap-1.5 px-3 py-2 text-xs sm:text-sm border border-red-300 text-red-600 rounded-lg hover:bg-red-50 transition-colors font-medium dark:text-red-400 dark:hover:bg-red-900/30"
                    >
                      <Unlink size={14} />
                      Unlink
                    </button>

                    <button
                      onClick={() => handleForceDeleteDevice(device.id)}
                      data-testid={`force-delete-device-${device.id}`}
                      title="Hapus device permanen (admin only). Gunakan saat ONT diganti/sudah tidak ada di ACS."
                      className="flex items-center justify-center gap-1.5 px-3 py-2 text-xs sm:text-sm border border-red-600 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium"
                    >
                      <X size={14} />
                      <span className="hidden sm:inline">Force Delete</span>
                      <span className="sm:hidden">Delete</span>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Link Device Dialog */}
      {showLinkDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full mx-4 max-h-[80vh] overflow-hidden dark:bg-slate-800">
            <div className="p-6 border-b border-gray-200 dark:border-slate-700">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-slate-100">Link Device to Customer</h2>
              <p className="text-sm text-gray-500 mt-1 dark:text-slate-400">Select an unassigned device to link</p>
            </div>
            
            <div className="p-6 overflow-y-auto max-h-96">
              {availableDevices.length === 0 ? (
                <div className="text-center py-8">
                  <Router className="mx-auto text-gray-300 mb-3" size={40} />
                  <p className="text-gray-500 dark:text-slate-400">No unassigned devices available</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {availableDevices.map((device) => (
                    <div
                      key={device.id}
                      className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer dark:border-slate-700 dark:hover:bg-slate-700/50"
                      onClick={() => handleLinkDevice(device.id)}
                    >
                      <div>
                        <p className="font-medium text-gray-900 dark:text-slate-100">{device.model}</p>
                        <p className="text-sm text-gray-500 dark:text-slate-400">SN: {device.serial_number}</p>
                        <p className="text-xs text-gray-400 font-mono">{device.mac_address}</p>
                      </div>
                      <span className={`px-3 py-1 rounded-md text-xs font-medium ${
                        device.status === 'online'
                          ? 'bg-green-50 text-green-700'
                          : 'bg-gray-50 text-gray-700'
                      }`}>
                        {device.status}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            {/* [MANUAL_ENTRY_2026-02] Manual serial input for new ONT not yet informed to ACS */}
            {showManualForm && (
              <div className="p-6 border-t border-gray-200 bg-indigo-50/40 dark:bg-slate-900/40 dark:border-slate-700">
                <h3 className="text-sm font-semibold text-indigo-900 dark:text-indigo-300 mb-2">Input Manual (ONT belum inform ke ACS)</h3>
                <p className="text-xs text-gray-500 dark:text-slate-400 mb-3">Isi Serial dan Model ONT baru. Nanti otomatis ke-sync setelah ONT online.</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-gray-600 dark:text-slate-400">Serial Number *</label>
                    <input
                      type="text"
                      data-testid="manual-serial-input"
                      value={manualSerial}
                      onChange={(e) => setManualSerial(e.target.value)}
                      placeholder="ZICG278AF418"
                      className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md text-sm dark:bg-slate-800 dark:border-slate-600 dark:text-slate-100"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-600 dark:text-slate-400">Model *</label>
                    <input
                      type="text"
                      data-testid="manual-model-input"
                      value={manualModel}
                      onChange={(e) => setManualModel(e.target.value)}
                      placeholder="M69X XPON / F663NV3a / HG8245H"
                      className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md text-sm dark:bg-slate-800 dark:border-slate-600 dark:text-slate-100"
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="text-xs text-gray-600 dark:text-slate-400">MAC Address (opsional)</label>
                    <input
                      type="text"
                      data-testid="manual-mac-input"
                      value={manualMac}
                      onChange={(e) => setManualMac(e.target.value)}
                      placeholder="B4:E4:6B:8A:F4:18"
                      className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md text-sm dark:bg-slate-800 dark:border-slate-600 dark:text-slate-100"
                    />
                  </div>
                </div>
              </div>
            )}

            <div className="p-6 border-t border-gray-200 flex justify-between gap-2 dark:border-slate-700">
              {!showManualForm ? (
                <button
                  onClick={() => setShowManualForm(true)}
                  data-testid="open-manual-form-btn"
                  className="px-4 py-2 border border-indigo-300 text-indigo-700 rounded-lg hover:bg-indigo-50 text-sm font-medium dark:border-indigo-500 dark:text-indigo-300 dark:hover:bg-indigo-900/30"
                >
                  + Input Manual (belum di ACS)
                </button>
              ) : (
                <button
                  onClick={() => { setShowManualForm(false); setManualSerial(''); setManualModel(''); setManualMac(''); }}
                  data-testid="cancel-manual-form-btn"
                  className="px-4 py-2 text-gray-600 hover:text-gray-800 text-sm dark:text-slate-400 dark:hover:text-slate-200"
                >
                  Batal Manual
                </button>
              )}
              <div className="flex gap-2">
                {showManualForm && (
                  <button
                    onClick={handleLinkManual}
                    disabled={manualSubmitting || !manualSerial.trim() || !manualModel.trim()}
                    data-testid="submit-manual-form-btn"
                    className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
                  >
                    {manualSubmitting ? 'Menyimpan...' : 'Daftarkan Device'}
                  </button>
                )}
                <button
                  onClick={() => { setShowLinkDialog(false); setShowManualForm(false); }}
                  data-testid="close-link-dialog-btn"
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 dark:border-slate-600 dark:hover:bg-slate-700/50"
                >
                  Tutup
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tickets Tab */}
      {activeTab === 'tickets' && (
        <div className="space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between gap-3">
            <h2 className="text-base sm:text-lg font-semibold text-gray-900 min-w-0 truncate dark:text-slate-100">
              Tiket Customer ({tickets.length})
            </h2>
            {tickets.length > 0 && (
              <button
                onClick={() => navigate('/app/tickets', { state: { prefilledCustomer: customer } })}
                className="flex items-center justify-center gap-1.5 px-3 sm:px-4 py-2 border border-indigo-300 text-indigo-600 text-sm rounded-lg hover:bg-indigo-50 transition-colors font-medium whitespace-nowrap flex-shrink-0 dark:text-indigo-400 dark:hover:bg-indigo-900/30"
              >
                <Plus size={16} />
                <span className="hidden xs:inline sm:inline">Buat Tiket Baru</span>
                <span className="xs:hidden sm:hidden">Baru</span>
              </button>
            )}
          </div>

          {/* Tickets List */}
          {ticketsLoading ? (
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-12 text-center dark:bg-slate-800 dark:border-slate-700">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
              <p className="mt-4 text-gray-500 dark:text-slate-400">Memuat tiket...</p>
            </div>
          ) : tickets.length === 0 ? (
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-12 text-center dark:bg-slate-800 dark:border-slate-700">
              <Ticket className="mx-auto text-gray-300 mb-4" size={48} />
              <h3 className="text-lg font-medium text-gray-900 mb-2 dark:text-slate-100">Belum Ada Tiket</h3>
              <p className="text-sm text-gray-500 mb-4 dark:text-slate-400">
                Belum ada tiket pemasangan atau problem yang terdaftar untuk customer ini
              </p>
              <button
                onClick={() => navigate('/app/tickets', { state: { prefilledCustomer: customer } })}
                className="inline-flex items-center justify-center gap-1.5 px-4 py-2 border border-indigo-300 text-indigo-600 rounded-lg hover:bg-indigo-50 transition-colors font-medium dark:text-indigo-400 dark:hover:bg-indigo-900/30"
              >
                <Plus size={16} />
                Buat Tiket Baru
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {tickets.map((ticket) => {
                const getStatusColor = (status) => {
                  switch (status) {
                    case 'OPEN': return 'bg-blue-100 text-blue-800';
                    case 'PENDING': return 'bg-yellow-100 text-yellow-800';
                    case 'CLOSE': return 'bg-green-100 text-green-800';
                    default: return 'bg-gray-100 text-gray-800';
                  }
                };

                const getTypeIcon = (type) => {
                  switch (type) {
                    case 'PEMASANGAN': return '🔧';
                    case 'PROBLEM': return '⚠️';
                    case 'REQUEST': return '📝';
                    default: return '🎫';
                  }
                };

                return (
                  <div
                    key={ticket.id}
                    onClick={() => navigate(`/app/tickets/${ticket.id}`)}
                    className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 hover:shadow-md transition-shadow cursor-pointer dark:bg-slate-800 dark:border-slate-700"
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-start gap-3">
                        <span className="text-2xl">{getTypeIcon(ticket.ticket_type)}</span>
                        <div>
                          <h3 className="font-semibold text-gray-900 dark:text-slate-100">
                            {ticket.ticket_type}
                          </h3>
                          <p className="text-sm text-gray-500 dark:text-slate-400">#{ticket.ticket_number}</p>
                        </div>
                      </div>
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(ticket.status)}`}>
                        {ticket.status}
                      </span>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                      <div>
                        <span className="text-gray-500 dark:text-slate-400">Teknisi:</span>
                        <span className="ml-2 font-medium">
                          {ticket.assigned_to_name || '-'}
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-500 dark:text-slate-400">Prioritas:</span>
                        <span className="ml-2 font-medium">{ticket.priority}</span>
                      </div>
                      <div>
                        <span className="text-gray-500 dark:text-slate-400">Dibuat:</span>
                        <span className="ml-2 font-medium">
                          {new Date(ticket.created_at).toLocaleDateString('id-ID')}
                        </span>
                      </div>
                      {ticket.closed_at && (
                        <div>
                          <span className="text-gray-500 dark:text-slate-400">Ditutup:</span>
                          <span className="ml-2 font-medium">
                            {new Date(ticket.closed_at).toLocaleDateString('id-ID')}
                          </span>
                        </div>
                      )}
                    </div>

                    {ticket.notes && (
                      <div className="mt-3 pt-3 border-t border-gray-100 dark:border-slate-700">
                        <p className="text-sm text-gray-600 line-clamp-2 dark:text-slate-300">{ticket.notes}</p>
                      </div>
                    )}

                    {ticket.pending_reason && (
                      <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg dark:bg-yellow-900/30">
                        <p className="text-sm text-yellow-800">
                          <strong>Kendala:</strong> {ticket.pending_reason}
                        </p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CustomerDetail;
